import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ColumnsType } from "@similarweb/pro-logic-components";

import { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import type { BenchmarkUrlParams } from "app/pages/benchmark-tracker/types/paramsTypes";
import { useTranslation } from "pro-features/components/WithTranslation";

import { type ICommonData, type IDomainData, useCommonColumnsConfig } from "../../../hooks/useCommonColumnsConfig";

import type { IRankChangeData } from "./useRankChangeColumnConfig";
import { useRankChangeColumnConfig } from "./useRankChangeColumnConfig";
import { useShareColumnConfig, type IShareColumnData } from "./useShareColumnConfig";

export type Data = ICommonData & IDomainData & IRankChangeData & IShareColumnData;

export const useColumnsConfig = () => {
    const translate = useTranslation();
    const {
        params: { granularity },
    } = useCurrentStateAndParams<BenchmarkUrlParams>();
    const shareColumn = useShareColumnConfig();
    const [rankPoP, rankYoY] = useRankChangeColumnConfig();
    const { highlightedDomainColumn, popColumn, rankColumn, trendColumn, yoyColumn } = useCommonColumnsConfig();

    return useMemo(
        () =>
            [
                rankColumn,
                highlightedDomainColumn,
                {
                    key: "share-group",
                    align: "right",
                    title: translate("bt.report.metric.share.view.header.title.currentPeriod"),
                    children: [shareColumn],
                },
                {
                    ...trendColumn,
                    width: 100,
                    minWidth: 100,
                    align: "left",
                },
                {
                    key: "pop-change-group",
                    title: translate(
                        granularity === BenchmarkGranularity.MONTHLY
                            ? "bt.report.metric.share.view.header.title.month.over.month"
                            : "bt.report.metric.share.view.header.title.week.over.week",
                    ),
                    children: [
                        {
                            ...popColumn,
                            title: translate(
                                "bt.report.channels.share-of-channel-traffic-competitors-table.share-change",
                            ),
                            width: 100,
                            minWidth: 100,
                        },
                        rankPoP,
                    ],
                },
                {
                    key: "yoy-change-group",
                    title: translate("bt.report.metric.share.view.header.title.year.over.year"),
                    children: [
                        {
                            ...yoyColumn,
                            title: translate(
                                "bt.report.channels.share-of-channel-traffic-competitors-table.share-change",
                            ),
                            width: 100,
                            minWidth: 100,
                        },
                        rankYoY,
                    ],
                },
            ] satisfies ColumnsType<ICommonData & IDomainData & IShareColumnData & IRankChangeData>,
        [
            rankColumn,
            highlightedDomainColumn,
            shareColumn,
            translate,
            trendColumn,
            granularity,
            popColumn,
            rankPoP,
            yoyColumn,
            rankYoY,
        ],
    );
};
